/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, {
  useState,
  useEffect,
  Fragment,
  useMemo,
  MutableRefObject,
  useRef,
  FC
} from 'react';
import ReactDayPicker, { DayModifiers, Modifiers } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import dateFnsFormat from 'date-fns/format';
import {
  add as addToDate,
  min as minOfDates,
  max as maxOfDates,
  startOfMonth,
  startOfYear,
  endOfMonth,
  startOfDay,
  endOfDay,
  isToday,
  isBefore,
  isAfter,
  startOfWeek,
  endOfWeek
} from 'date-fns';
import _ from 'lodash';

import { Input, Button } from '@scuf/common';

import { HOCLocalization } from '../../utils/HOC/HOCLocalization';

import 'react-day-picker/lib/style.css';

import './AdvDateRangeSelector.scss';
import TranslationsUtility from '../../utils/translations/TranslationsUtility';
import { useTranslation } from '@scuf/localization';
import moment from 'moment';

export interface IDateRangeSelectorProps {
  /* Label to be displayed, default Duration */
  label?: string;
  /* From value for the Range Picker */
  fromValue?: Date;
  /* To value for the Range Picker */
  toValue?: Date;
  /* Date format, to display in the picker */
  dateFormat?: string;
  /* Max allowed days for range selection */
  maxAllowedDays?: number;
  indicator?: 'required' | 'optional';
  disabled?: boolean;
  closeOnClickOutside?: boolean;
  allowedToValueUpto?: Date;
  hideLabel?: boolean;
  roundedDatePicker?: boolean;
  /*Hides quick links displayed on right (Today, This Week etc) */
  hideQuickLinks?: boolean;
  /**
   * Called when apply button is clicked
   */
  onSetDateRange: (from: Date, to: Date) => void;
}

interface IDateRangeSelectorShortCut {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  helperText?: string;
}

export const AdvDateRangeSelector: FC<IDateRangeSelectorProps> = (
  props: IDateRangeSelectorProps
) => {
  const [currFromValue, setCurrFromValue] = useState<Date>(() => {
    // Check if props.fromValue is undefined, if so, return a default Date
    if (props.fromValue === undefined) {
      return new Date(); // You can replace this with any default value you prefer
    }
    return props.fromValue;
  });
  const [t, lng] = useTranslation();
  const [currToValue, setCurrToValue] = useState<Date>(() => {
    // Check if props.toValue is undefined, if so, return a default Date
    if (props.toValue === undefined) {
      return new Date(); // You can replace this with any default value you prefer
    }
    return props.toValue;
  });

  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [onClickDate, setOnClickDate] = useState(false);
  const [disableApply, setDisableApply] = useState(true);
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState('');

  const todayDate = moment().format('YYYY-MM-DD');
  let lastYearDate = moment()
    .subtract(1, 'years')
    .format('YYYY-MM-DD');
  const selectorRef = useRef() as MutableRefObject<HTMLDivElement>;
  const overRef = useRef() as MutableRefObject<HTMLDivElement>;

  useEffect(() => {
    if (
      props.maxAllowedDays !== undefined && // Checking if props.maxAllowedDays is defined
      moment(currToValue).diff(moment(currFromValue), 'days') <=
        props.maxAllowedDays && // Checking condition 1
      moment(currToValue) >= moment(currFromValue) // Checking condition 2
    ) {
      setDisableApply(false);
    } else {
      setDisableApply(true);
    }

    if (
      props.maxAllowedDays !== undefined &&
      moment(currToValue).diff(moment(currFromValue), 'days') >
        props.maxAllowedDays
    ) {
      setError(true);
      setErrorText(
        t('datePickerErrorMsg', { days: Math.floor(props.maxAllowedDays / 30) })
      );
    } else if (
      props.maxAllowedDays !== undefined &&
      moment(currToValue).diff(moment(currFromValue), 'days') <=
        props.maxAllowedDays
    ) {
      setError(false);
    }
    if (moment(currToValue) < moment(currFromValue)) {
      setError(true);
      setErrorText(t('datePickerGreaterErrorMessage'));
    }
  }, [currFromValue, currToValue]);
  const closePopupOnClickOutside = (e: MouseEvent) => {
    const { closeOnClickOutside, disabled } = props;
    if (disabled) return;
    const clickTarget = e.target as HTMLElement;
    const selector = selectorRef.current;
    if (!selector.contains(clickTarget) && closeOnClickOutside)
      setPopupOpen(false);
  };

  useEffect(() => {
    document.addEventListener('click', closePopupOnClickOutside);
    return () => {
      document.removeEventListener('click', closePopupOnClickOutside);
    };
  }, []);

  useEffect(() => {
    setCurrFromValue(props.fromValue || new Date());
    setCurrToValue(props.toValue || new Date());
  }, [props.fromValue, props.toValue, popupOpen]);

  const formatDate = (date: Date, format: string) => {
    let formatString = '';
    switch (format) {
      case 'DD/MM/YYYY':
        formatString = 'dd/MM/yyyy';
        break;
      case 'MM/DD/YYYY':
        formatString = 'MM/dd/yyyy';
        break;
      case 'YYYY/MM/DD':
        formatString = 'yyyy/MM/dd';
        break;
      default:
        formatString = 'dd/MM/yyyy';
    }
    return dateFnsFormat(date, formatString);
  };

  const shortCuts = useMemo<{ [l: string]: IDateRangeSelectorShortCut }>(() => {
    const { maxAllowedDays, allowedToValueUpto } = props;
    const shortcuts: { [l: string]: IDateRangeSelectorShortCut } = {
      Today: {
        label: t('Today'),
        onClick: () => {
          let startDate = startOfDay(new Date());
          let endDate = new Date(Date.now());
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        },
        disabled:
          allowedToValueUpto &&
          isBefore(startOfDay(allowedToValueUpto), startOfDay(new Date()))
      },
      Yesterday: {
        label: t('Yesterday'),
        onClick: () => {
          let startDate = startOfDay(addToDate(new Date(), { days: -1 }));
          let endDate = endOfDay(addToDate(new Date(), { days: -1 }));
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        },
        disabled:
          allowedToValueUpto &&
          isBefore(startOfDay(allowedToValueUpto), startOfDay(new Date()))
      },
      LastWeek: {
        label: t('LastWeek'),
        onClick: () => {
          let startDate = startOfDay(
            startOfWeek(addToDate(new Date(), { weeks: -1 }), {
              weekStartsOn: 0
            })
          );
          let endDate = endOfDay(
            endOfWeek(addToDate(new Date(), { weeks: -1 }), { weekStartsOn: 0 })
          );
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        },
        disabled:
          allowedToValueUpto &&
          isBefore(startOfDay(allowedToValueUpto), startOfDay(new Date()))
      },
      ThisMonth: {
        label: t('ThisMonth'),
        onClick: () => {
          let startDate = startOfDay(startOfMonth(new Date()));
          let endDate = new Date();
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        },
        disabled:
          allowedToValueUpto &&
          isBefore(startOfDay(allowedToValueUpto), startOfDay(new Date()))
      },
      LastMonth: {
        label: t('LastMonth'),
        onClick: () => {
          let startDate = startOfMonth(
            startOfDay(addToDate(new Date(), { months: -1 }))
          );
          let endDate = endOfMonth(endOfDay(startDate));
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        }
      },
      Last3Months: {
        label: t('Last3Months'),
        onClick: () => {
          let startDate = startOfDay(
            startOfMonth(addToDate(new Date(), { months: -3 }))
          );
          let endDate = endOfDay(
            endOfMonth(addToDate(new Date(), { months: -1 }))
          );
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        }
      },
      Last6Months: {
        label: t('Last6Months'),
        onClick: () => {
          let possibleStartDates = [
            startOfDay(startOfMonth(addToDate(new Date(), { months: -6 })))
          ];
          if (maxAllowedDays) {
            possibleStartDates.push(
              startOfDay(addToDate(new Date(), { days: -1 * maxAllowedDays }))
            );
          }
          let startDate = maxOfDates(possibleStartDates);
          let endDate = endOfDay(
            endOfMonth(addToDate(new Date(), { months: -1 }))
          );
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        },
        disabled:
          (maxAllowedDays &&
            isAfter(
              startOfDay(addToDate(new Date(), { days: -1 * maxAllowedDays })),
              startOfDay(startOfMonth(addToDate(new Date(), { months: -6 })))
            )) ||
          undefined
      },
      YearToDate: {
        label: t('YearToDate'),
        onClick: () => {
          let possibleStartDates = [startOfDay(startOfYear(new Date()))];
          if (maxAllowedDays) {
            possibleStartDates.push(
              startOfDay(addToDate(new Date(), { days: -1 * maxAllowedDays }))
            );
          }
          let possibleEndDates = [new Date()];
          // if (allowedToValueUpto) {
          //   possibleEndDates.push(allowedToValueUpto);
          // }
          let startDate = maxOfDates(possibleStartDates);
          let endDate = minOfDates(possibleEndDates);
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        },
        disabled:
          (maxAllowedDays &&
            isAfter(
              startOfDay(addToDate(new Date(), { days: -1 * maxAllowedDays })),
              startOfDay(startOfYear(new Date()))
            )) ||
          undefined
      },
      LastYear: {
        label: t('LastYear'),
        onClick: () => {
          let possibleStartDates = [
            startOfDay(addToDate(new Date(), { years: -1 }))
          ];
          if (maxAllowedDays) {
            possibleStartDates.push(
              startOfDay(addToDate(new Date(), { days: -1 * maxAllowedDays }))
            );
          }
          let possibleEndDates = [new Date()];
          if (allowedToValueUpto) {
            possibleEndDates.push(allowedToValueUpto);
          }
          let startDate = maxOfDates(possibleStartDates);
          let endDate = minOfDates(possibleEndDates);
          setCurrFromValue(startDate);
          setCurrToValue(endDate);
        },
        disabled:
          (maxAllowedDays &&
            isAfter(
              startOfDay(addToDate(new Date(), { days: -1 * maxAllowedDays })),
              startOfDay(addToDate(new Date(), { years: -1 }))
            )) ||
          undefined
      }
    };
    return shortcuts;
  }, [props]);

  const onClickFromDate = (
    day: Date,
    modifiers: DayModifiers,
    e: React.MouseEvent
  ) => {
    if (!modifiers.disabled) {
      setCurrFromValue(startOfDay(day));
    }
  };

  const onClickToDate = (
    day: Date,
    modifiers: DayModifiers,
    e: React.MouseEvent
  ) => {
    if (!modifiers.disabled) {
      if (!isToday(day)) {
        setCurrToValue(endOfDay(day));
        setOnClickDate(true);
        if (!isAfter(currFromValue, addToDate(day, { days: -1 * 365 }))) {
          let possibleStartDates = [startOfDay(addToDate(day, { years: -1 }))];
          if (props.maxAllowedDays) {
            possibleStartDates.push(
              startOfDay(addToDate(day, { days: -1 * props.maxAllowedDays }))
            );
          }
          let startDate = maxOfDates(possibleStartDates);
          setCurrFromValue(startDate);
        }
      } else {
        setCurrToValue(new Date());
        setOnClickDate(true);
        if (
          !isAfter(currFromValue, addToDate(new Date(), { days: -1 * 365 }))
        ) {
          let possibleStartDates = [
            startOfDay(addToDate(new Date(), { years: -1 }))
          ];
          if (props.maxAllowedDays) {
            possibleStartDates.push(
              startOfDay(
                addToDate(new Date(), { days: -1 * props.maxAllowedDays })
              )
            );
          }
          let startDate = maxOfDates(possibleStartDates);
          setCurrFromValue(startDate);
        }
      }
    }
  };

  const onClickCancel = () => {
    setCurrFromValue(props.fromValue ? props.fromValue : new Date());
    setCurrToValue(props.toValue ? props.toValue : new Date());
    setPopupOpen(false);
  };

  const handleFocus = () => {
    if (props.disabled) return;
    setPopupOpen(true);
  };

  const fromModifiers = useMemo<Partial<Modifiers>>(() => {
    const { maxAllowedDays, allowedToValueUpto } = props;

    let disabledAfterDates = [
      allowedToValueUpto,
      addToDate(currToValue, { days: 0 })
    ];

    const filteredDates: Date[] = (disabledAfterDates || []).filter(
      (date) => date !== undefined
    ) as Date[];

    const modifier: Partial<Modifiers> = {
      highlighted: {
        from: currFromValue,
        to: currToValue
      },
      selected: currFromValue,
      otherselected: currToValue,
      disabled: {
        after: minOfDates(
          (disabledAfterDates || []).filter(
            (date) => date !== undefined
          ) as Date[]
        )
      }
    };
    return modifier;
  }, [currFromValue, currToValue, popupOpen, props.fromValue]);

  const toModifiers = useMemo<Partial<Modifiers>>(() => {
    const { allowedToValueUpto } = props;
    let disabledAfterDates = [new Date(Date.now()), allowedToValueUpto];

    const modifier: Partial<Modifiers> = {
      highlighted: {
        from: currFromValue,
        to: currToValue
      },
      selected: currToValue,
      otherselected: currFromValue,
      disabled: {
        before: addToDate(currFromValue, { years: 3 }),
        after: minOfDates(
          (disabledAfterDates || []).filter(
            (date) => date !== undefined
          ) as Date[]
        )
      }
    };
    return modifier;
  }, [currFromValue, currToValue, popupOpen, props.toValue]);

  const onClickApply = () => {
    props.onSetDateRange(currFromValue, currToValue);
    setPopupOpen(false);
  };

  const inputDisplayValue = useMemo(
    () =>
      `${formatDate(
        props.fromValue ? props.fromValue : new Date(),
        props.dateFormat ? props.dateFormat : ''
      )} - ${formatDate(
        props.toValue ? props.toValue : new Date(),
        props.dateFormat ? props.dateFormat : ''
      )}`,
    [props, props.fromValue, props.toValue, popupOpen]
  );

  return (
    <Fragment>
      <div className="date-range-selector" ref={selectorRef}>
        <div className="date-range-select-container">
          {!props.hideLabel && <span>{props.label || t('Duration')}</span>}
          <div className="ui date-picker-input">
            <div className="overlay-position-wrap">
              <div className="datetime-input-wrap">
                <Input
                  className={
                    props.roundedDatePicker
                      ? 'range-picker-input picker-input datetime-date fluid roundedBorder'
                      : 'range-picker-input picker-input datetime-date fluid'
                  }
                  icon={'calendar'}
                  iconPosition={'left'}
                  readOnly={true}
                  uncontrolled={true}
                  indicator={props.indicator}
                  placeholder={`${props.dateFormat} - ${props.dateFormat}`}
                  value={inputDisplayValue}
                  onFocus={handleFocus}
                />
              </div>
              {popupOpen && (
                <div className={`calendar-overlay unreserved `} ref={overRef}>
                  <div className={`picker-wrapper`}>
                    <div className="range-calendar-wrapper">
                      <ReactDayPicker
                        onDayClick={onClickFromDate}
                        modifiers={fromModifiers}
                        month={currFromValue}
                        localeUtils={MomentLocaleUtils}
                        locale={'En-Us'}
                        firstDayOfWeek={0}
                      />
                      <ReactDayPicker
                        onDayClick={onClickToDate}
                        modifiers={toModifiers}
                        month={currToValue}
                        localeUtils={MomentLocaleUtils}
                        locale={'En-Us'}
                        firstDayOfWeek={0}
                      />
                    </div>
                    <span className="datepicker-err-msg">
                      {error ? errorText : ''}
                    </span>
                    <div className={`bottom-wrapper`}>
                      <div className="text-display-wrapper">
                        <span className="overlay-text-display text-from-val">
                          {t('From')}
                          {':'}
                        </span>
                        <span className="overlay-text-display text-from-val">
                          {formatDate(
                            currFromValue,
                            props.dateFormat ? props.dateFormat : ''
                          )}
                        </span>
                        <span className="overlay-text-display icon-right-caret"></span>
                        <span className="overlay-text-display text-from-val">
                          {t('to')}
                          {':'}
                        </span>
                        <span className="overlay-text-display text-from-val">
                          {formatDate(
                            currToValue,
                            props.dateFormat ? props.dateFormat : ''
                          )}
                        </span>
                      </div>
                      <div className={`button-wrapper`}>
                        <Button
                          className="apply-button"
                          type="primary"
                          disabled={disableApply}
                          onClick={() => {
                            onClickApply();
                          }}
                        >
                          {t('apply')}
                        </Button>
                        <Button
                          className="cancel-button"
                          type="link"
                          onClick={() => {
                            onClickCancel();
                          }}
                        >
                          {t('cancel')}
                        </Button>
                      </div>
                    </div>
                  </div>
                  {!props.hideQuickLinks ? (
                    <div className={`shortcuts-wrapper`}>
                      {_.map(shortCuts, (v: IDateRangeSelectorShortCut, k) => (
                        <div
                          onClick={() => {
                            if (!v.disabled) {
                              v.onClick();
                            }
                          }}
                          key={k}
                          className={`shortcut-btn shortcut-${k} ${
                            v.disabled ? 'disabled' : ''
                          }`}
                        >
                          {v.label}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

AdvDateRangeSelector.defaultProps = {
  fromValue: startOfDay(addToDate(new Date(), { months: -1 })),
  toValue: new Date(),
  dateFormat: 'DD/MM/YYYY',
  allowedToValueUpto: new Date()
};

export default HOCLocalization(AdvDateRangeSelector);
